/**
 * Serwis API komunikatora (backend/api/komunikator.py + komunikator_admin.py,
 * blueprint pod /api/komunikator).
 *
 * Konwencja zgodna z reszta serwisow w repo:
 *  - korzystamy ze wspolnej instancji axios `api` (services/api.js). baseURL ma
 *    juz /api, wiec sciezki podajemy BEZ wiodacego /api,
 *  - interceptor sam dokłada X-CSRF-Token (POST/DELETE), X-Device-Token i
 *    location_id,
 *  - funkcje zwracaja juz ROZPAKOWANA zawartosc koperty backendu
 *    ({success, data, message}) - czyli to, co siedzi w `data`,
 *  - w catch rzucamy Error z czytelnym komunikatem po polsku.
 *
 * UWAGA (kosztowna lekcja z tego repo): to jest axios, NIE fetch. Odpowiedz nie
 * ma pola `.ok` - ma `.status`. Interceptor api.js rzuca goly `Error` z polami
 * {status, data, errorCode}, a NIE obiekt axiosowy z `.response`, dlatego
 * komunikat wyciagamy z error.message / error.data, a nie z error.response.data.
 */

import api from './api';

const BAZA = 'komunikator';

/**
 * Konfiguracja dla zadan TLA (polling co 3 s / 20 s):
 *  - silent: true  -> interceptor api.js NIE pokazuje czerwonego dymka. Bez tego
 *    kazde 500 (np. niewykonana migracja) albo zerwane Wi-Fi zamienia sie w serie
 *    dymkow bez konca, takze na ekranie POS - a tam ma byc cisza. Widget pokazuje
 *    blad wlasnym paskiem (.kom-pasek-bledu).
 *  - noRetry: true -> wylacza automatyczne 2 ponowienia z interceptora. Widget ma
 *    wlasny wykladniczy backoff (do 60 s), wiec retry tylko potrajalby ruch do
 *    gunicorna (2 workery sync) dokladnie wtedy, gdy backend juz sie dusi.
 */
const CICHY = { silent: true, noRetry: true };

/** Ujednolicony blad: interceptor api.js daje juz gotowy `message`. */
const komunikatBledu = (error, zapasowy) => {
  const blad = new Error(
    error?.data?.message ||
    error?.data?.error ||
    error?.message ||
    zapasowy
  );
  blad.status = error?.status;
  blad.errorCode = error?.errorCode;
  blad.isAuthError = !!error?.isAuthError;
  blad.isTimeout = !!error?.isTimeout;
  return blad;
};

/** Rozpakowanie koperty {success, data, message} z weryfikacja statusu HTTP. */
const zawartosc = (res) => {
  const okStatus = res && typeof res.status === 'number' && res.status >= 200 && res.status < 300;
  if (!okStatus) return null;
  const cialo = res.data;
  if (cialo && typeof cialo === 'object' && 'data' in cialo) return cialo.data;
  return cialo || null;
};

// ---------------------------------------------------------------------------
// ROZMOWY
// ---------------------------------------------------------------------------

/**
 * GET /rozmowy - lista rozmow uzytkownika.
 * WAZNE: to jedyny endpoint, ktory ZAKLADA kanaly i uczestnictwo. Trzeba go
 * wywolac raz po zalogowaniu, inaczej /nieprzeczytane nie ma czego liczyc.
 * @returns {Promise<{rozmowy: Array, total: number}>}
 */
export const pobierzRozmowy = async (signal) => {
  try {
    const res = await api.get(`${BAZA}/rozmowy`, { signal, ...CICHY });
    const d = zawartosc(res) || {};
    return { rozmowy: Array.isArray(d.rozmowy) ? d.rozmowy : [], total: Number(d.total) || 0 };
  } catch (error) {
    throw komunikatBledu(error, 'Nie udało się pobrać listy rozmów');
  }
};

/**
 * GET /rozmowy/<id>/wiadomosci - historia MALEJACO po id (najnowsze pierwsze).
 * @param {number} rozmowaId
 * @param {{beforeId?: number, limit?: number, signal?: AbortSignal}} opcje
 * @returns {Promise<{wiadomosci: Array, ma_wiecej: boolean, najstarsze_id: number|null}>}
 */
export const pobierzWiadomosci = async (rozmowaId, opcje = {}) => {
  const { beforeId = null, limit = 50, signal = undefined } = opcje;
  try {
    const params = { limit };
    if (beforeId) params.before_id = beforeId;
    const res = await api.get(`${BAZA}/rozmowy/${rozmowaId}/wiadomosci`, { params, signal });
    const d = zawartosc(res) || {};
    return {
      wiadomosci: Array.isArray(d.wiadomosci) ? d.wiadomosci : [],
      ma_wiecej: !!d.ma_wiecej,
      najstarsze_id: d.najstarsze_id || null,
    };
  } catch (error) {
    throw komunikatBledu(error, 'Nie udało się pobrać wiadomości');
  }
};

/** POST /rozmowy/<id>/wiadomosci - wyslanie tekstu. Zwraca gotowy obiekt wiadomosci. */
export const wyslijWiadomosc = async (rozmowaId, tresc) => {
  try {
    const res = await api.post(`${BAZA}/rozmowy/${rozmowaId}/wiadomosci`, { tresc });
    const d = zawartosc(res) || {};
    return d.wiadomosc || null;
  } catch (error) {
    throw komunikatBledu(error, 'Nie udało się wysłać wiadomości');
  }
};

/**
 * POST /rozmowy/<id>/zalaczniki - multipart, pole 'plik' (+ opcjonalne 'tresc').
 * Backend konwertuje HEIC/HEIF z iPhone'a na JPEG, obraca wg EXIF, kompresuje
 * i robi miniature.
 * Odpowiedz ma INNY ksztalt niz zwykla wiadomosc - normalizujemy ja tutaj do
 * tego samego formatu, zeby UI mial jedna sciezke renderowania.
 */
export const wyslijZalacznik = async (rozmowaId, plik, tresc = '', onPostep = null) => {
  try {
    const formData = new FormData();
    formData.append('plik', plik);
    if (tresc) formData.append('tresc', tresc);

    const config = {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    };
    if (onPostep) {
      config.onUploadProgress = (e) => {
        if (!e || !e.total) return;
        onPostep(Math.min(99, Math.round((e.loaded * 100) / e.total)));
      };
    }

    const res = await api.post(`${BAZA}/rozmowy/${rozmowaId}/zalaczniki`, formData, config);
    const d = zawartosc(res) || {};
    const z = d.zalacznik || {};
    return {
      id: d.wiadomosc_id,
      rozmowa_id: d.rozmowa_id,
      autor_login: d.autor_login,
      autor_nazwa: d.autor_nazwa || d.autor_login,
      wlasna: true,
      typ: d.typ || 'plik',
      tresc: d.tresc || null,
      usunieta: false,
      utworzono: new Date().toISOString().slice(0, 19).replace('T', ' '),
      zalaczniki: z.id ? [{
        id: z.id,
        nazwa: z.plik_nazwa,
        mime_type: z.mime_type,
        rozmiar_bajty: z.rozmiar_bajty,
        szerokosc: z.szerokosc,
        wysokosc: z.wysokosc,
        ma_miniature: !!z.ma_miniature,
        oryginal_heic: !!z.oryginal_heic,
      }] : [],
    };
  } catch (error) {
    throw komunikatBledu(error, 'Nie udało się wysłać załącznika');
  }
};

/** POST /rozmowy/dm - utworz lub otworz rozmowe 1:1 (idempotentne). */
export const otworzRozmowePrywatna = async (login) => {
  try {
    const res = await api.post(`${BAZA}/rozmowy/dm`, { login });
    const d = zawartosc(res) || {};
    return d.rozmowa || null;
  } catch (error) {
    throw komunikatBledu(error, 'Nie udało się otworzyć rozmowy');
  }
};

/** POST /rozmowy/<id>/czytaj - przesuniecie wskaznika przeczytania (tylko w gore). */
export const oznaczPrzeczytane = async (rozmowaId, doId = null) => {
  try {
    const cialo = doId ? { do_id: doId } : {};
    const res = await api.post(`${BAZA}/rozmowy/${rozmowaId}/czytaj`, cialo);
    return zawartosc(res) || {};
  } catch (error) {
    throw komunikatBledu(error, 'Nie udało się oznaczyć jako przeczytane');
  }
};

/** POST /rozmowy/<id>/ukryj - zamkniecie rozmowy prywatnej (400 dla kanalow). */
export const ukryjRozmowe = async (rozmowaId) => {
  try {
    const res = await api.post(`${BAZA}/rozmowy/${rozmowaId}/ukryj`, {});
    return zawartosc(res) || {};
  } catch (error) {
    throw komunikatBledu(error, 'Nie udało się zamknąć rozmowy');
  }
};

/** DELETE /wiadomosci/<id> - soft delete, tylko autor. */
export const usunWiadomosc = async (wiadomoscId) => {
  try {
    const res = await api.delete(`${BAZA}/wiadomosci/${wiadomoscId}`);
    return zawartosc(res) || {};
  } catch (error) {
    throw komunikatBledu(error, 'Nie udało się usunąć wiadomości');
  }
};

/** GET /kontakty - uzytkownicy do rozpoczecia rozmowy (z rozmowa_id, jesli juz istnieje). */
export const pobierzKontakty = async (q = '', signal = undefined) => {
  try {
    const res = await api.get(`${BAZA}/kontakty`, { params: q ? { q } : {}, signal });
    const d = zawartosc(res) || {};
    return Array.isArray(d.kontakty) ? d.kontakty : [];
  } catch (error) {
    throw komunikatBledu(error, 'Nie udało się pobrać listy kontaktów');
  }
};

/** GET /nieprzeczytane - LEKKI endpoint pollingu (1 zapytanie SQL, 0 zapisow). */
export const pobierzNieprzeczytane = async (signal) => {
  try {
    const res = await api.get(`${BAZA}/nieprzeczytane`, { signal, ...CICHY });
    const d = zawartosc(res) || {};
    return { total: Number(d.total) || 0, rozmowy: Array.isArray(d.rozmowy) ? d.rozmowy : [] };
  } catch (error) {
    throw komunikatBledu(error, 'Brak połączenia z komunikatorem');
  }
};

// ---------------------------------------------------------------------------
// ZALACZNIKI - pobieranie przez axios (blob), nie przez goly <img src>
// ---------------------------------------------------------------------------
//
// Dlaczego blob, a nie bezposredni URL w <img src>:
// require_auth (auth.py) sprawdza X-Device-Token, a przegladarka NIE wysle tego
// naglowka przy zwyklym <img>. Fallback na token w sesji istnieje, ale bywa
// pusty - wtedy KAZDY obrazek dawalby 403 i czyscil sesje. Przez axios naglowki
// leca komplet.

const CACHE_BLOBOW = new Map(); // klucz -> objectURL
const MAX_BLOBOW = 60;

const zapiszWCache = (klucz, url) => {
  CACHE_BLOBOW.set(klucz, url);
  while (CACHE_BLOBOW.size > MAX_BLOBOW) {
    const najstarszy = CACHE_BLOBOW.keys().next().value;
    const doZwolnienia = CACHE_BLOBOW.get(najstarszy);
    CACHE_BLOBOW.delete(najstarszy);
    try { URL.revokeObjectURL(doZwolnienia); } catch (e) { /* nieistotne */ }
  }
};

/**
 * GET /zalacznik/<id>[?thumb=1] jako objectURL do <img src>.
 * Wynik jest cache'owany (LRU, 60 pozycji) - przewijanie historii w gore i w
 * dol nie generuje kolejnych zapytan.
 */
export const zalacznikBlobUrl = async (zalacznikId, thumb = false) => {
  const klucz = `${zalacznikId}:${thumb ? 1 : 0}`;
  if (CACHE_BLOBOW.has(klucz)) return CACHE_BLOBOW.get(klucz);
  try {
    const res = await api.get(`${BAZA}/zalacznik/${zalacznikId}`, {
      params: thumb ? { thumb: 1 } : {},
      responseType: 'blob',
      timeout: 60000,
    });
    const url = URL.createObjectURL(res.data);
    zapiszWCache(klucz, url);
    return url;
  } catch (error) {
    throw komunikatBledu(error, 'Nie udało się pobrać załącznika');
  }
};

/** Pobranie zalacznika na dysk uzytkownika (dziala takze dla plikow nie-obrazkowych). */
export const pobierzZalacznik = async (zalacznikId, nazwaPliku = 'zalacznik') => {
  try {
    const res = await api.get(`${BAZA}/zalacznik/${zalacznikId}`, {
      responseType: 'blob',
      timeout: 120000,
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = nazwaPliku;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // revoke z opoznieniem - natychmiastowe potrafi uciac pobieranie w Safari
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch (e) { /* nieistotne */ } }, 30000);
    return true;
  } catch (error) {
    throw komunikatBledu(error, 'Nie udało się pobrać pliku');
  }
};

const komunikatorService = {
  pobierzRozmowy,
  pobierzWiadomosci,
  wyslijWiadomosc,
  wyslijZalacznik,
  otworzRozmowePrywatna,
  oznaczPrzeczytane,
  ukryjRozmowe,
  usunWiadomosc,
  pobierzKontakty,
  pobierzNieprzeczytane,
  zalacznikBlobUrl,
  pobierzZalacznik,
};

export default komunikatorService;
